<!--
  首页 - 商品瀑布流
  用途：主功能一：以瀑布流展示商品图片，点击进入商品详情
  用法：路由 `/`；点击商品卡片跳转 `/product/:id`
-->
<template>
  <div class="home-page">
    <section class="hero">
      <!--
        Hero 双栏布局
        用途：左侧放标题与按钮，右侧空位放大展示红色 Logo
      -->
      <div class="hero-content">
        <div class="hero-copy">
          <h1>{{ t('home.title') }}</h1>
          <p>{{ t('home.subtitle') }}</p>
          <div class="hero-actions">
            <a class="btn btn-primary" href="#product-gallery">{{ t('home.browseProducts') }}</a>
            <router-link class="btn btn-ghost" to="/cases">{{ t('common.cases') }}</router-link>
          </div>
        </div>
        <div class="hero-visual" aria-hidden="false">
          <img class="hero-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
        </div>
      </div>
    </section>

    <section id="product-gallery" class="gallery-section">
      <div class="section-head">
        <h2>{{ t('home.galleryTitle') }}</h2>
        <p>{{ t('home.galleryHint') }}</p>
      </div>

      <!--
        商品类别筛选
        用途：按 categoryKey 过滤瀑布流商品；“全部”显示所有商品
        用法：点击筛选项更新 activeCategory，products 会自动重新计算
      -->
      <div class="category-filter" :aria-label="t('home.filterLabel')">
        <span class="filter-label">{{ t('home.filterLabel') }}</span>
        <div class="filter-chips">
          <button
            type="button"
            class="filter-chip"
            :class="{ active: activeCategory === 'all' }"
            @click="setCategory('all')"
          >
            {{ t('home.filterAll') }}
          </button>
          <button
            v-for="item in categoryOptions"
            :key="item.key"
            type="button"
            class="filter-chip"
            :class="{ active: activeCategory === item.key }"
            @click="setCategory(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <!--
        瀑布流入口
        products 已按当前语言本地化并完成类别筛选；item-click 用于跳转详情页
      -->
      <p v-if="loading" class="filter-empty">{{ t('common.loading') }}</p>
      <p v-else-if="error" class="filter-empty">{{ error }}</p>
      <WaterImageFall
        v-else-if="products.length"
        :images="products"
        :column-width="columnWidth"
        :gutter="16"
        @item-click="goDetail"
      />
      <p v-else class="filter-empty">{{ t('home.filterEmpty') }}</p>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import WaterImageFall from '@/components/WaterImageFall.vue'
import { fetchCategories, fetchProducts } from '@/services/api'
import { categoryLabel, localizeProductFromApi } from '@/utils/localize'
import logoUrl from '@/assets/logo.png'

const router = useRouter()
const { t, locale } = useI18n()
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)

/** 从 API 拉取的原始商品 / 类别列表 */
const rawProducts = ref([])
const categories = ref([])
const loading = ref(true)
const error = ref('')
const activeCategory = ref('all')

/** 筛选项：仅展示当前商品实际用到的类别，名称来自 API */
const categoryOptions = computed(() => {
  void locale.value
  const used = new Set(rawProducts.value.map((p) => p.categoryKey).filter(Boolean))
  return categories.value
    .filter((c) => used.has(c.id))
    .map((c) => ({
      key: c.id,
      label: categoryLabel(c, locale.value) || c.id
    }))
})

/** 本地化 + 类别筛选后的商品列表 */
const products = computed(() => {
  void locale.value
  const localized = rawProducts.value.map((item) =>
    localizeProductFromApi(item, locale.value, t, categories.value)
  )
  if (activeCategory.value === 'all') return localized
  return localized.filter((item) => item.categoryKey === activeCategory.value)
})

const setCategory = (key) => {
  activeCategory.value = key
}

const columnWidth = computed(() => {
  if (windowWidth.value < 640) return 160
  if (windowWidth.value < 960) return 220
  return 280
})

const onResize = () => {
  windowWidth.value = window.innerWidth
}

const goDetail = (product) => {
  if (!product?.id) return
  router.push({ name: 'product-detail', params: { id: product.id } })
}

/** 加载商品与类别数据（来自 PHP API） */
const loadProducts = async () => {
  loading.value = true
  error.value = ''
  try {
    const [productData, categoryData] = await Promise.all([
      fetchProducts(),
      fetchCategories()
    ])
    rawProducts.value = productData
    categories.value = categoryData
  } catch (e) {
    error.value = e.message || t('common.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  loadProducts()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
.home-page {
  width: 100%;
}

.hero {
  background:
    radial-gradient(circle at top right, rgba(210, 7, 22, 0.12), transparent 42%),
    linear-gradient(180deg, #fff 0%, #fafafa 100%);
  border-bottom: 1px solid var(--color-border);
}

.hero-content {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 56px 24px 48px;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(180px, 0.85fr);
  align-items: center;
  gap: 32px;
  text-align: left;
}

.hero-copy {
  min-width: 0;
}

.hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 右侧大尺寸 Logo，填补标题旁空位 */
.hero-logo {
  width: clamp(160px, 28vw, 280px);
  height: auto;
  aspect-ratio: 1;
  object-fit: contain;
  display: block;
}

.hero h1 {
  max-width: 640px;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: var(--color-text);
  margin-bottom: 14px;
}

.hero p {
  max-width: 520px;
  color: var(--color-text-secondary);
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 28px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 128px;
  padding: 12px 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--color-primary);
  color: #fff;
}

.btn-primary:hover {
  background: var(--color-primary-hover);
}

.btn-ghost {
  background: #fff;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-ghost:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.gallery-section {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 40px 24px 64px;
}

.section-head {
  margin-bottom: 20px;
  text-align: left;
}

.section-head h2 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.section-head p {
  color: var(--color-text-secondary);
  font-size: 15px;
}

.category-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 28px;
  text-align: left;
}

.filter-label {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chip {
  border: 1px solid var(--color-border);
  background: #fff;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.filter-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.filter-chip.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.filter-empty {
  padding: 48px 16px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 15px;
}

@media (max-width: 860px) {
  .hero-content {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-top: 40px;
    padding-bottom: 36px;
  }

  .hero-visual {
    order: -1;
    justify-content: flex-start;
  }

  .hero-logo {
    width: clamp(120px, 36vw, 180px);
  }
}

@media (max-width: 640px) {
  .hero-content,
  .gallery-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero-content {
    padding-top: 36px;
    padding-bottom: 32px;
  }
}
</style>
